/**
 * 🟥 CUADRO NEGRO V7 — DeploysPanel.
 *
 * Modal que lista los deployments del repo (GitHub Deployments API) y
 * permite re-disparar y ver logs (con summary IA).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Rocket, X, Loader2, ExternalLink, RotateCcw, FileText, Sparkles,
  CheckCircle2, XCircle, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

const STATE_STYLES = {
  ready: { color: 'text-emerald-400', Icon: CheckCircle2, label: 'Ready' },
  error: { color: 'text-rose-400', Icon: XCircle, label: 'Error' },
  building: { color: 'text-amber-400', Icon: Loader2, label: 'Building' },
  queued: { color: 'text-sky-400', Icon: Clock, label: 'Queued' },
  pending: { color: 'text-slate-400', Icon: Clock, label: 'Pending' },
  inactive: { color: 'text-slate-500', Icon: Clock, label: 'Inactive' },
  unknown: { color: 'text-slate-500', Icon: Clock, label: 'Unknown' },
};

export default function DeploysPanel({ moduleId, githubSource, isEs = true, onClose }) {
  const [deploys, setDeploys] = useState([]);
  const [provider, setProvider] = useState('none');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // id:action
  const [logDeploy, setLogDeploy] = useState(null);
  const [logData, setLogData] = useState(null);
  const [loadingLog, setLoadingLog] = useState(false);

  const repoLabel = githubSource ? `${githubSource.owner}/${githubSource.repo}` : '';

  const fetchDeploys = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/deploys`, { params: { module_id: moduleId } });
      setDeploys(res.data?.deploys || []);
      setProvider(res.data?.provider || 'none');
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Deploys falló: ${detail}` : `Deploys failed: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const id = setTimeout(() => { fetchDeploys(); }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  const handleRedeploy = async (d) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(isEs ? `¿Re-disparar deploy #${d.id}?` : `Re-deploy #${d.id}?`)) return;
    setBusy(`${d.id}:redeploy`);
    try {
      const res = await axios.post(`${API}/deploys/redeploy`, {
        module_id: moduleId, deploy_id: d.id,
      });
      toast.success(
        isEs
          ? `Re-deploy creado #${res.data?.new_deploy_id}`
          : `Re-deploy created #${res.data?.new_deploy_id}`
      );
      fetchDeploys();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Re-deploy falló: ${detail}` : `Re-deploy failed: ${detail}`);
    } finally {
      setBusy(null);
    }
  };

  const handleLogs = async (d) => {
    setLogDeploy(d);
    setLogData(null);
    setLoadingLog(true);
    try {
      const res = await axios.get(`${API}/deploys/logs`, {
        params: { module_id: moduleId, deploy_id: d.id },
      });
      setLogData(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(isEs ? `Log falló: ${detail}` : `Log failed: ${detail}`);
      setLogDeploy(null);
    } finally {
      setLoadingLog(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[8500] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="deploys-panel"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-4xl h-[88vh] bg-gradient-to-b from-slate-900 to-slate-950 border border-sky-500/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-gradient-to-r from-sky-900/40 to-cyan-900/30">
          <div className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-sky-300" />
            <div>
              <h2 className="text-base font-bold text-white">{isEs ? 'Deploys' : 'Deploys'}</h2>
              <p className="text-[11px] text-sky-300/80">{repoLabel} · provider: {provider}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="deploys-close-btn"
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="flex justify-center py-12 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : deploys.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-12">
              {isEs
                ? 'Aún no hay deploys en este repositorio. Configura un workflow de deploy o un proveedor (Vercel, Netlify, etc.) que cree GitHub Deployments.'
                : 'No deploys in this repo yet.'}
            </div>
          ) : (
            deploys.map((d) => {
              const meta = STATE_STYLES[d.state] || STATE_STYLES.unknown;
              const Icon = meta.Icon;
              const animate = d.state === 'building';
              return (
                <div
                  key={d.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-slate-900 border border-slate-800 hover:border-sky-500/40"
                  data-testid={`deploy-row-${d.id}`}
                >
                  <Icon className={`w-5 h-5 shrink-0 ${meta.color} ${animate ? 'animate-spin' : ''}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-white font-medium">{d.environment}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${meta.color} bg-slate-800`}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-400 truncate font-mono">
                      {d.branch && <>#{d.branch} · </>}
                      {d.commit_sha?.slice(0, 7)}
                      {d.updated_at && <> · {new Date(d.updated_at).toLocaleString()}</>}
                    </div>
                    {d.description && (
                      <div className="text-[11px] text-slate-500 truncate mt-0.5">{d.description}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {d.url && (
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 rounded-md text-sky-300 hover:text-white hover:bg-slate-800"
                        title={isEs ? 'Abrir deploy' : 'Open deploy'}
                        data-testid={`deploy-open-${d.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => handleLogs(d)}
                      data-testid={`deploy-logs-${d.id}`}
                      className="p-1.5 rounded-md bg-indigo-600/80 hover:bg-indigo-500 text-white"
                      title={isEs ? 'Ver logs + summary IA' : 'View logs + AI summary'}
                    >
                      <FileText className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRedeploy(d)}
                      disabled={busy === `${d.id}:redeploy`}
                      data-testid={`deploy-redeploy-${d.id}`}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-60"
                      title={isEs ? 'Re-disparar deploy' : 'Re-deploy'}
                    >
                      {busy === `${d.id}:redeploy`
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <RotateCcw className="w-3 h-3" />}
                      {isEs ? 'Re-deploy' : 'Re-deploy'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Log viewer */}
        {logDeploy && (
          <div
            className="fixed inset-0 z-[8600] bg-black/85 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) { setLogDeploy(null); setLogData(null); } }}
          >
            <div className="w-full max-w-3xl max-h-[88vh] bg-slate-950 border border-indigo-500/30 rounded-2xl flex flex-col">
              <header className="flex items-center justify-between px-4 py-2 border-b border-slate-800">
                <div className="text-sm text-white">
                  Deploy #{logDeploy.id} · {logDeploy.environment}
                </div>
                <button
                  type="button"
                  onClick={() => { setLogDeploy(null); setLogData(null); }}
                  className="p-1.5 rounded text-slate-400 hover:text-white"
                  data-testid="deploy-log-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </header>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {loadingLog ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-300" />
                  </div>
                ) : logData ? (
                  <>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-indigo-400 mb-1 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> {isEs ? 'Resumen IA' : 'AI Summary'}
                      </div>
                      <pre className="text-xs whitespace-pre-wrap text-slate-200 bg-slate-900 p-3 rounded border border-slate-800">
                        {logData.summary || (isEs ? '(sin resumen)' : '(no summary)')}
                      </pre>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                        {isEs ? 'Historial de status' : 'Status timeline'}
                      </div>
                      <div className="space-y-1">
                        {(logData.statuses || []).map((s, idx) => (
                          <div key={idx} className="text-[11px] p-2 rounded bg-slate-900 border border-slate-800">
                            <span className="font-mono text-emerald-300">{s.state}</span>
                            <span className="text-slate-400 ml-2">{s.description || ''}</span>
                            {s.url && (
                              <a href={s.url} target="_blank" rel="noreferrer" className="ml-2 text-sky-400 underline">
                                {s.url.slice(0, 60)}…
                              </a>
                            )}
                            <span className="text-slate-500 block mt-0.5">{s.created_at}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
